import posthog, { type BeforeSendFn } from "posthog-js";

// --- Error-tracking noise filter ---------------------------------------
// Mirror of the filter in apps/ui/website/src/posthog.ts. Drops benign,
// non-actionable errors (browser warnings, cancelled fetches, opaque
// cross-origin errors, extension noise) client-side so Error Tracking
// reflects real bugs. Keep NARROW; do not filter real app errors. Keep in
// sync with the website copy.
const EXCEPTION_NOISE_PATTERNS: RegExp[] = [
  /ResizeObserver loop/i,
  /\bAbortError\b|signal is aborted without reason|The user aborted a request/i,
  /^Script error\.?$/i,
  /__firefox__|chrome-extension:|moz-extension:|runtime\.sendMessage/i,
  // Extension families confirmed external in the 7/16 triage (mirrored
  // from the website copy): webextension-polyfill probing, crypto-wallet
  // injectors, content-script visibility pings, and Outlook SafeLink /
  // assistive DOM crawler replays.
  /getManifest is not a function/i,
  /window\.ethereum|wallet must has at least one account|provider is disconnected from all chains/i,
  /contentScriptVisibilityChanged/i,
  /Object Not Found Matching Id:/,
  // React #418 hydration text mismatch on eden.so is browser auto-translate
  // (Chrome/Safari rewriting the English page for non-English-locale visitors)
  // — external + cosmetic (React recovers). Confirmed via heavy non-English
  // locale skew. MARKETING-ONLY: deliberately NOT filtered in the app's
  // posthog.ts, so genuine app hydration regressions still surface.
  /react\.dev\/errors\/418|Minified React error #418/i,
  // Meta's Instagram/Facebook in-app-browser bridge script (injected as a
  // WKUserScript; frames `sendPageHideMessage`/`sendDataToNative`) derefs
  // `window.webkit.messageHandlers` unguarded on pagehide and throws once
  // the WKWebView bridge is torn down. Most volume is IG-ad traffic on
  // eden.so/bootcamp (referrer l.instagram.com); not our code.
  /window\.webkit\.messageHandlers/i,
];

// Local dev servers report into the same PostHog project and are NOT caught
// by `filterTestAccounts` — exceptions from loopback hosts are never prod
// signal; drop them at the source. Mirrors the website copy.
function isLocalDevHost(): boolean {
  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname;
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";
}

function collectExceptionMessages(properties: Record<string, unknown>): string[] {
  const messages: string[] = [];
  const list = properties.$exception_list;
  if (Array.isArray(list)) {
    for (const entry of list) {
      const value = (entry as { value?: unknown; type?: unknown } | null)?.value;
      const type = (entry as { value?: unknown; type?: unknown } | null)?.type;
      if (typeof value === "string") messages.push(value);
      if (typeof type === "string") messages.push(type);
    }
  }
  const values = properties.$exception_values;
  if (Array.isArray(values)) {
    for (const value of values) if (typeof value === "string") messages.push(value);
  }
  const message = properties.$exception_message;
  if (typeof message === "string") messages.push(message);
  return messages;
}

// Drop benign exception events; pass everything else through untouched.
// Defensive: any failure here keeps the event.
const dropNoisyExceptions: BeforeSendFn = (event) => {
  try {
    if (!event || event.event !== "$exception") return event;
    if (isLocalDevHost()) return null;
    const messages = collectExceptionMessages(event.properties ?? {});
    const isNoise = messages.some((message) =>
      EXCEPTION_NOISE_PATTERNS.some((pattern) => pattern.test(message)),
    );
    if (isNoise) return null;
  } catch {
    // Swallow filter errors and keep the event.
  }
  return event;
};

// Marketing-site PostHog client. Mirrors the website's config in
// apps/ui/website/src/posthog.ts so the anonymous visitor's distinct
// id is shared via a `.eden.so` cookie when they cross from
// `eden.so` → `app.eden.so`. Once they sign in, the
// website's `AuthProvider` calls `posthog.identify(userId, …)`,
// which links the anonymous funnel events captured here onto the
// authenticated user record without losing the pre-sign-in trail.
//
// `api_host` points at our reverse proxy (`obs.eden.so`) to dodge
// adblockers; `ui_host` is the actual PostHog UI used when clicking
// through "view in PostHog" on captured events.
export const posthogOptions = {
  api_host: "https://obs.eden.so",
  ui_host: "https://us.i.posthog.com",
  defaults: "2026-01-30",
  before_send: dropNoisyExceptions,
} as const;

const posthogProjectToken = process.env.GATSBY_PUBLIC_POSTHOG_PROJECT_TOKEN?.trim();

let initialized = false;

/**
 * Initialise the marketing-site PostHog client. Safe to call from
 * `gatsby-browser` hooks — guards against SSR (Gatsby's build runs
 * Node), missing token, and double-init when Gatsby's hot reloader
 * re-runs the browser bundle.
 */
export function initLandingPostHog(): void {
  if (typeof window === "undefined") return;
  if (!posthogProjectToken) return;
  if (initialized) return;
  posthog.init(posthogProjectToken, posthogOptions);
  initialized = true;
}

function isPostHogReady(): boolean {
  if (typeof window === "undefined") return false;
  return (posthog as unknown as { __loaded?: boolean }).__loaded === true;
}

/**
 * Fire a named landing-CTA event before navigating. PostHog's
 * autocapture would catch the click anyway, but a stable event name
 * (`landing_cta_click`) makes it trivial to build a funnel that
 * connects "click on landing" → "viewed onboarding step" → "signed in"
 * → "subscribed". Always non-fatal: any thrown error is swallowed so
 * a misconfigured SDK can't block the navigation.
 */
export function captureLandingCtaClick(
  cta: string,
  properties: Record<string, string | number | boolean | null | undefined> = {},
): void {
  if (!isPostHogReady()) return;
  try {
    posthog.capture("landing_cta_click", { cta, ...properties });
  } catch {
    // No-op: analytics must never block navigation.
  }
}

export function captureLandingTestimonialEvent(
  event: string,
  properties: Record<string, string | number | boolean | null | undefined> = {},
): void {
  if (!isPostHogReady()) return;
  try {
    posthog.capture(event, { surface: "landing_testimonials", ...properties });
  } catch {
    // No-op: analytics must never block form submission.
  }
}

/**
 * Attach the server-assigned landing-page arm to every subsequent event and
 * record an explicit exposure. Unlike the click helpers, this intentionally
 * permits PostHog to queue the event while its network client is still
 * loading; `onClientEntry` has already called `initLandingPostHog` before the
 * page component mounts.
 */
export function registerLandingExperimentVariant(
  experiment: string,
  variant: "control" | "canvas" | "paths",
): void {
  if (typeof window === "undefined" || !initialized) return;
  try {
    const properties = {
      landing_page_experiment: experiment,
      landing_page_variant: variant,
    };
    posthog.register(properties);
    posthog.capture("landing_page_experiment_viewed", properties);
  } catch {
    // No-op: analytics must never interfere with the landing page.
  }
}

/**
 * Tag every later event in this browser with the `/ad/` page and its copy
 * version (`ad_page`, `ad_page_version`), so replays, clicks and funnels can
 * be split by version while a page is being optimized. Queues like
 * `registerLandingExperimentVariant` does.
 */
export function registerAdPageVersion(page: string, version: string): void {
  if (typeof window === "undefined" || !initialized) return;
  try {
    posthog.register({ ad_page: page, ad_page_version: version });
  } catch {
    // No-op: analytics must never interfere with the landing page.
  }
}

/**
 * Tag every later event with the hero headline an `/ad/` page showed
 * (`ad_hero_angle`, the ad's `utm_term` when the page has a headline for
 * it), so signups can be split by the headline people saw.
 */
export function registerAdHeroAngle(angle: string): void {
  if (typeof window === "undefined" || !initialized) return;
  try {
    posthog.register({ ad_hero_angle: angle });
  } catch {
    // No-op: analytics must never interfere with the landing page.
  }
}

/**
 * One `landing_section_viewed` per section a visitor reaches on an `/ad/`
 * page: the scroll funnel for that page version.
 */
export function captureLandingSectionViewed(section: string, sectionIndex: number): void {
  if (typeof window === "undefined" || !initialized) return;
  try {
    posthog.capture("landing_section_viewed", { section, section_index: sectionIndex });
  } catch {
    // No-op: analytics must never interfere with the landing page.
  }
}

/**
 * A visitor reaching card N (2 or later) of a swipeable row on an `/ad/`
 * page, by swiping or tapping its dot: once per card per row, so we can see
 * who browses past the first card ("testimonials-top", "think", ...).
 */
export function captureLandingRowSwiped(row: string, cardPosition: number): void {
  if (typeof window === "undefined" || !initialized) return;
  try {
    posthog.capture("landing_row_swiped", { row, card_position: cardPosition });
  } catch {
    // No-op: analytics must never interfere with the landing page.
  }
}

export { posthog };
